import { useEffect, useRef, useState } from 'react';

import Keycloak from 'keycloak-js';

/**
 * Tempo mínimo entre requisições de renovação (em ms) para evitar flood
 */
const MIN_RENEWAL_DELAY_MS = 60 * 1000;

/**
 * Margem de tolerância para compensar latência de rede e overhead de execução.
 */
const TOKEN_LIFETIME_TOLERANCE_SECONDS = 5;

export type SessionStatus = 'normal' | 'ending' | 'expired';

export interface SessionState {
    status: SessionStatus;
    expirationTimestamp: number | null;
}

/**
 * Hook que encapsula toda a lógica de renovação automática do token Keycloak.
 *
 * 1. Agendamento Periódico (Renovação de Access Token):
 *    Calcula-se o tempo restante do access token e agenda-se sua renovação
 *    (quando faltar ~80% do tempo ou mínimo de 60s). Quando o disparo ocorre,
 *    solicitamos a renovação do token (force refresh: -1).
 *
 * 2. Avaliação de Sessão (Detecção de Ociosidade/Fim de Sessão):
 *    Sempre que o token é renovado, o keycloak-js aciona um callback de sucesso
 *    (onAuthRefreshSuccess). Nesse momento, avaliamos o refresh token. O refresh token tem
 *    seu próprio tempo de vida e deve ser estendido pelo servidor a cada renovação. Se
 *    notarmos que o tempo de vida restante do refresh token diminuiu continuamente
 *    (descontando uma tolerância de rede), significa que o servidor parou de renová-lo
 *    (ex: sessão inativa). A sessão entra no estado 'ending'.
 *
 * 3. Timer de Segurança (Session Expiration Timer):
 *    Em cada avaliação, um temporizador é (re)agendado com o tempo de vida exato restante
 *    do refresh token. Se o temporizador chegar ao fim sem ser cancelado por uma nova
 *    renovação bem-sucedida, confirmamos que a sessão expirou irreversivelmente no
 *    backend, alterando o status para 'expired'.
 */
export function useTokenRenewal(keycloak: Keycloak) {
    const renewalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sessionExpirationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previousRefreshTokenLifetimeSeconds = useRef<number | null>(null);

    const [sessionState, setSessionState] = useState<SessionState>({
        status: 'normal',
        expirationTimestamp: null,
    });

    /**
     * Retorna o tempo de vida restante do refresh token em segundos.
     */
    const getRefreshTokenRemainingSeconds = () => {
        const { refreshTokenParsed } = keycloak;
        const nowSeconds = Math.floor(Date.now() / 1000);
        const timeSkew = keycloak.timeSkew || 0;

        return (refreshTokenParsed?.exp || 0) + timeSkew - nowSeconds;
    };

    /**
     * Encerra os timers de renovação do token.
     */
    const stopTokenRenewal = () => {
        if (renewalTimer.current !== null) {
            clearTimeout(renewalTimer.current);

            renewalTimer.current = null;
        }

        if (sessionExpirationTimer.current !== null) {
            clearTimeout(sessionExpirationTimer.current);

            sessionExpirationTimer.current = null;
        }

        previousRefreshTokenLifetimeSeconds.current = null;
    };

    /**
     * Calcula o delay em millisegundos para a próxima renovação do token.
     */
    const calculateRenewalDelay = () => {
        const tokenParsed = keycloak.tokenParsed;

        if (!tokenParsed?.exp) return MIN_RENEWAL_DELAY_MS;

        const timeSkewSeconds = keycloak.timeSkew ?? 0;
        const tokenExpirationTimestampMs = (tokenParsed.exp + timeSkewSeconds) * 1000;
        const tokenTimeRemainingMs = tokenExpirationTimestampMs - Date.now();
        const defaultRenewalDelayMs = tokenTimeRemainingMs * 0.2;

        return Math.max(defaultRenewalDelayMs, MIN_RENEWAL_DELAY_MS);
    };

    /**
     * Atualiza a referência de tempo de vida do refresh token com o valor atual.
     *
     * Chamada em dois momentos:
     *  - Login bem-sucedido (startTokenRenewal) → estabelece a referência inicial
     *  - Pós-renovação (evaluateSessionStatus) → atualiza para o próximo ciclo
     */
    const updateTokenLifetimeReference = () => (previousRefreshTokenLifetimeSeconds.current = getRefreshTokenRemainingSeconds());

    /**
     * Agenda ou reagenda o timer de segurança de expiração do refresh token.
     * O delay é o tempo de vida atual do refresh token — dispara exatamente quando
     * ele expiraria caso nenhuma renovação ocorra antes.
     */
    const scheduleSessionExpirationTimer = () => {
        // limpa o timer anterior antes de criar um novo
        if (sessionExpirationTimer.current !== null) clearTimeout(sessionExpirationTimer.current);

        const refreshTokenLifetimeMs = getRefreshTokenRemainingSeconds() * 1000;

        sessionExpirationTimer.current = setTimeout(() => {
            const remainingSeconds = getRefreshTokenRemainingSeconds();

            if (remainingSeconds <= 0) {
                console.warn('useTokenRenewal: timer de segurança confirmou expiração do refresh token');

                stopTokenRenewal();

                setSessionState({
                    status: 'expired',
                    expirationTimestamp: null,
                });
            } else {
                console.warn(`useTokenRenewal: timer de segurança disparou prematuramente (${remainingSeconds}s restantes) — ignorando`);
            }
        }, refreshTokenLifetimeMs);
    };

    /**
     * Avalia o status da sessão comparando o tempo de vida atual do refresh token
     * com o registrado no ciclo anterior.
     *
     * Se o lifetime atual for menor que o anterior, descontando tolerância,
     * significa que o Keycloak não renovou o refresh token, então a sessão está finalizando.
     *
     * Deve ser chamada APÓS updateTokenLifetimeReference ter sido chamada ao menos uma vez.
     */
    const evaluateSessionStatus = () => {
        const currentLifetimeSeconds = getRefreshTokenRemainingSeconds();

        const isSessionEnding =
            previousRefreshTokenLifetimeSeconds.current !== null &&
            currentLifetimeSeconds < previousRefreshTokenLifetimeSeconds.current - TOKEN_LIFETIME_TOLERANCE_SECONDS;

        updateTokenLifetimeReference();

        scheduleSessionExpirationTimer();

        if (isSessionEnding) {
            setSessionState({
                status: 'ending',
                expirationTimestamp: Date.now() + currentLifetimeSeconds * 1000,
            });
        } else {
            setSessionState({
                status: 'normal',
                expirationTimestamp: null,
            });
        }
    };

    /**
     * Agenda a renovação do token.
     * Calcula o delay com base em 20% do tempo restante do token (mínimo 60s),
     * agenda a renovação via setTimeout, e re-agenda recursivamente após cada refresh.
     * Em caso de token expirado, marca a sessão como expirada.
     */
    const scheduleRenewal = (onTokenRefreshed: (token: string) => void) => {
        // se token expirou, altera state para expirado para exibir modal de sessão expirada
        if (keycloak.isTokenExpired()) {
            stopTokenRenewal();

            setSessionState((prev) => ({ ...prev, status: 'expired' }));

            return;
        }

        const delayMs = calculateRenewalDelay();

        // reseta o timer de renovação, se existia
        if (renewalTimer.current !== null) clearTimeout(renewalTimer.current);

        // cria novo timer
        renewalTimer.current = setTimeout(async () => {
            try {
                await keycloak.updateToken(-1); // -1 = force refresh

                onTokenRefreshed(keycloak.token as string);

                scheduleRenewal(onTokenRefreshed);
            } catch (error) {
                console.error('useTokenRenewal: erro durante renovação do token', error);

                if (keycloak.isTokenExpired()) {
                    stopTokenRenewal();

                    setSessionState((prev) => ({ ...prev, status: 'expired' }));
                }
            }
        }, delayMs);
    };

    /**
     * Inicia o timer de renovação do token.
     * Fluxo:
     * 1. Encerra qualquer renovação ativa anterior
     * 2. Salva referência do lifetime do refresh token
     * 3. Avalia estado da sessão atual
     * 4. Registra callback onAuthRefreshSuccess para reavaliar a cada renovação
     * 5. Agenda a renovação periódica
     */
    const startTokenRenewal = (onTokenRefreshed: (token: string) => void) => {
        stopTokenRenewal();

        // define valor inicial do lifetime do refresh token para comparação
        updateTokenLifetimeReference();

        // avalia o estado da sessão atual
        evaluateSessionStatus();

        // listener de sucesso do refresh do token para reavaliar o estado da sessão
        keycloak.onAuthRefreshSuccess = () => evaluateSessionStatus();

        // agenda a renovação do token
        scheduleRenewal(onTokenRefreshed);
    };

    // limpa os timers ao desmontar o componente
    useEffect(() => {
        return () => stopTokenRenewal();
    }, []);

    return {
        startTokenRenewal,
        stopTokenRenewal,
        sessionState,
    };
}
